import {Dispatch, DispatchEntity} from "../../../model/dispatch";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {TransportCategory} from "../../../model/transport-category";
import {dispatchRequestStatus} from "../../../model/dispatch-request";
import {makeEntitySelectors} from "../base-entity-store";

export type DispatchSelectorProps = {
  regionId?: string
  category?: TransportCategory
  personId?: string
  vehicleId?: string
  trailerId?: string
  pickupSiteId?: string
  pickupSiteCompanyId?: string
  deliverySiteId?: string
  deliverySiteCompanyId?: string
  includeUnassigned?: boolean
};

const getRegionIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCategoryParam = createParamSelector<DispatchSelectorProps | undefined, TransportCategory | undefined>(params => params?.category);
const getPersonIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.personId);
const getVehicleIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.vehicleId);
const getTrailerIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.trailerId);
const getPickupSiteIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.pickupSiteId);
const getPickupSiteCompanyIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.pickupSiteCompanyId);
const getDeliverySiteIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.deliverySiteId);
const getDeliverySiteCompanyIdParam = createParamSelector<DispatchSelectorProps | undefined, string | undefined>(params => params?.deliverySiteCompanyId);
const getIncludeUnassignedParam = createParamSelector<DispatchSelectorProps | undefined, boolean | undefined>(params => params?.includeUnassigned);

export const dispatchesSelector = () => createSelector(
  [
    getUseQueryResultData<Dispatch[]>,
    getRegionIdParam,
    getCategoryParam,
    getPersonIdParam,
    getVehicleIdParam,
    getTrailerIdParam,
    getPickupSiteIdParam,
    getPickupSiteCompanyIdParam,
    getDeliverySiteIdParam,
    getDeliverySiteCompanyIdParam,
    getIncludeUnassignedParam,
  ],
  (
    data,
    regionId,
    category,
    personId,
    vehicleId,
    trailerId,
    pickupSiteId,
    pickupSiteCompanyId,
    deliverySiteId,
    deliverySiteCompanyId,
    includeUnassigned,
  ) => {
    const dispatches = selectDispatchesAndTransform(
      data ?? [], {
        regionId: regionId,
        category: category,
        personId: personId,
        vehicleId: vehicleId,
        trailerId: trailerId,
        pickupSiteId: pickupSiteId,
        pickupSiteCompanyId: pickupSiteCompanyId,
        deliverySiteId: deliverySiteId,
        deliverySiteCompanyId: deliverySiteCompanyId,
        includeUnassigned: includeUnassigned,
      }
    );

    let canceledRequests = 0
    for (const dispatch of dispatches) {
      const dispatchRequest = dispatch.dispatchRequest;
      if (dispatchRequest) {
        if (dispatchRequest.status === dispatchRequestStatus.pendingCancel || dispatchRequest.status === dispatchRequestStatus.canceled) {
          canceledRequests++
        }
      }
    }

    return {
      dispatches: dispatches,
      metadata: {
        total: dispatches.length,
        canceledRequests: canceledRequests,
      }
    }
  }
);

export const dispatchSelector = () => createSelector(
  [getUseQueryResultData<Dispatch>],
  (data) => {
    return data ? transformDispatch(data) : undefined;
  }
);

export const selectDispatches = (values?: Dispatch[], selector?: DispatchSelectorProps): Dispatch[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (v.regionId !== selector.regionId) {
        filterIn = false;
      }
    }

    if (selector?.category) {
      if (v.category !== selector.category.code) {
        filterIn = false;
      }
    }

    if (selector?.personId) {
      if (selector?.includeUnassigned && typeof v.personId === "undefined") {
        // include
      } else if (v.personId !== selector.personId) {
        filterIn = false;
      }
    }

    if (selector?.vehicleId) {
      if (v.vehicleId !== selector.vehicleId) {
        filterIn = false;
      }
    }

    if (selector?.trailerId) {
      if (v.trailerId !== selector.trailerId) {
        filterIn = false;
      }
    }

    if (selector?.pickupSiteId) {
      if (v.pickupSiteId !== selector.pickupSiteId) {
        filterIn = false;
      }
    }

    if (selector?.pickupSiteCompanyId) {
      if (v.pickupSiteCompanyId !== selector.pickupSiteCompanyId) {
        filterIn = false;
      }
    }

    if (selector?.deliverySiteId) {
      if (v.deliverySiteId !== selector.deliverySiteId) {
        filterIn = false;
      }
    }

    if (selector?.deliverySiteCompanyId) {
      if (v.deliverySiteCompanyId !== selector.deliverySiteCompanyId) {
        filterIn = false;
      }
    }

    return filterIn;
  });
};

export const transformDispatch = (value: Dispatch): DispatchEntity => {
  return DispatchEntity.from(value)
}

export const selectDispatchesAndTransform = (values?: Dispatch[], selector?: DispatchSelectorProps): DispatchEntity[] => {
  const selected = selectDispatches(values, selector);
  return selected.map(transformDispatch);
};

// const filter = (v : Dispatch, selector : DispatchSelectorProps | undefined) => {
//   let filterIn = true;
//
//   if (selector?.regionId) {
//     if (v.regionId !== selector.regionId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.category) {
//     if (v.category !== selector.category.code) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.personId) {
//     if (selector?.includeUnassigned && typeof v.personId === "undefined") {
//       // include
//     } else if (v.personId !== selector.personId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.vehicleId) {
//     if (v.vehicleId !== selector.vehicleId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.trailerId) {
//     if (v.trailerId !== selector.trailerId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.pickupSiteId) {
//     if (v.pickupSiteId !== selector.pickupSiteId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.pickupSiteCompanyId) {
//     if (v.pickupSiteCompanyId !== selector.pickupSiteCompanyId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.deliverySiteId) {
//     if (v.deliverySiteId !== selector.deliverySiteId) {
//       filterIn = false;
//     }
//   }
//
//   if (selector?.deliverySiteCompanyId) {
//     if (v.deliverySiteCompanyId !== selector.deliverySiteCompanyId) {
//       filterIn = false;
//     }
//   }
//
//   return filterIn;
// }
//
// const dispatchSelectors = makeEntitySelectors<
//   Dispatch,
//   DispatchEntity,
//   DispatchSelectorProps
// >({
//   defaultProps: {},
//
//   filter: filter,
//
//   transform: (value) => DispatchEntity.from(value),
// });
//
// export const dispatchesSelector =
//   (props?: DispatchSelectorProps) =>
//     dispatchSelectors.listSelectorWithParams(
//       props,
//     );
//
// export const dispatchSelector =
//   () => dispatchSelectors.singleSelector();
